import React, { useEffect, useCallback, useRef } from 'react';
import './ProjectLightbox.scss';

const ProjectLightbox = ({ items = [], startIndex = 0, onClose, onImageChange, startOpen = true }) => {
    const [index, setIndex] = React.useState(startIndex);
    const dialogRef = useRef(null);
    const lastFocusedRef = useRef(null);
    const captionRef = useRef(null);

    const prefetchAdjacentImages = useCallback((currentIndex) => {
        const nextIndex = (currentIndex + 1) % items.length;
        const prevIndex = (currentIndex - 1 + items.length) % items.length;

        // Prefetch next and previous images
        [nextIndex, prevIndex].forEach(idx => {
            const img = new Image();
            img.src = items[idx].src1200 || items[idx].src;
        });
    }, [items]);

    useEffect(() => {
        if (!startOpen) return;
        // Lock scroll and manage focus
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        lastFocusedRef.current = document.activeElement;
        // Focus the close button when dialog mounts
        const closeBtn = dialogRef.current?.querySelector('.plb-close');
        closeBtn?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
            // Restore focus to the element that opened the dialog
            if (lastFocusedRef.current && lastFocusedRef.current.focus) {
                lastFocusedRef.current.focus();
            }
        };
    }, [startOpen]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + items.length) % items.length);
            if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % items.length);

            // Basic focus trap inside the dialog
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const focusables = Array.from(focusable).filter((el) => !el.hasAttribute('disabled'));
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                } else if (!e.shiftKey && document.activeElement === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [items.length, onClose]);

    // Prefetch adjacent images when current image changes
    useEffect(() => {
        prefetchAdjacentImages(index);
        // Call parent callback when image index changes
        if (onImageChange) {
            onImageChange(index);
        }
    }, [index, prefetchAdjacentImages, onImageChange]);

    if (!items || items.length === 0) return null;

    return (
        <div className="plb-backdrop" role="dialog" aria-modal="true" aria-label="Image gallery" aria-describedby="plb-caption" ref={dialogRef}>
            {/* Live region for screen readers */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
                {`Image ${index + 1} of ${items.length}: ${items[index].caption}`}
            </div>
            <div className="plb-content">
                <button className="plb-close" onClick={onClose} aria-label="Close gallery" title="Close">×</button>

                <div className="plb-media">
                    {items.map((item, i) => (
                        <img
                            key={i}
                            src={item.src1200 || item.src}
                            srcSet={item.src400 ? `${item.src400} 400w, ${item.src800} 800w, ${item.src1200} 1200w` : undefined}
                            sizes="(max-width: 768px) 100vw, 800px"
                            alt={item.alt || ''}
                            style={{ display: i === index ? 'block' : 'none' }}
                            aria-hidden={i !== index}
                        />
                    ))}
                    {items[index].caption && <div className="plb-caption" id="plb-caption" ref={captionRef}>{items[index].caption}</div>}
                </div>

                <button className="plb-nav plb-prev" onClick={() => setIndex((i) => (i - 1 + items.length) % items.length)} aria-label="Previous image">‹</button>
                <button className="plb-nav plb-next" onClick={() => setIndex((i) => (i + 1) % items.length)} aria-label="Next image">›</button>

                <div className="plb-thumbs">
                    {items.map((it, i) => (
                        <button
                            key={i}
                            className={`plb-thumb ${i === index ? 'is-active' : ''}`}
                            onClick={() => setIndex(i)}
                            aria-label={`Go to image ${i + 1}`}
                        >
                            <img
                                src={it.src400 || it.src}
                                alt={it.alt || ''}
                                aria-hidden="true"
                                loading="lazy"
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ProjectLightbox;
